import React, { useCallback, useMemo } from 'react'
import { Form, Input, Button, Typography, App } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import AuthService from '@/libs/services/auth'

const { Title, Text } = Typography

const StyledForm = styled(Form)`
  width: 100%;
`

const SubmitButton = styled(Button)`
  height: 45px;
  background-color: #13c2c2;
  border-color: #13c2c2;
  &:hover {
    background-color: #36cfc9;
    border-color: #36cfc9;
  }
`

const FooterText = styled.div`
  text-align: center;
  margin-top: 24px;
`

const ForgotPasswordView = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [isFormValid, setIsFormValid] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const { notification } = App.useApp()

  const onFinish = useCallback(
    async values => {
      setLoading(true)
      try {
        const response = await AuthService.resetPassword({
          email: values.email,
        })

        if (response.status === 204) {
          notification.success({
            message: 'Success',
            description: 'A Password reset email has been sent successfully!',
            placement: 'topRight',
            duration: 3,
          })
          navigate('/auth/login', { replace: true })
        }
      } catch {
        notification.error({
          message: 'Error',
          description: 'Failed to reset password. Please try again.',
          placement: 'topRight',
          duration: 3,
        })
        form.setFields([
          {
            name: 'email',
            errors: ['Failed to reset password. Please try again.'],
          },
        ])
      } finally {
        setLoading(false)
      }
    },
    [navigate, form, notification]
  )

  const formRules = useMemo(
    () => ({
      email: [
        { required: true, message: 'Please input your email!' },
        { type: 'email', message: 'Please enter a valid email' },
      ],
    }),
    []
  )

  // Add form validation check on field changes
  const onFieldsChange = () => {
    const values = form.getFieldsValue()
    setIsFormValid(values.email)
  }

  return (
    <>
      <Title level={3}>Reset Password</Title>
      <Text type="secondary" style={{ marginBottom: 24, display: 'block' }}>
        Enter your email and we'll send you instructions to reset your password
      </Text>

      <StyledForm
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        onFieldsChange={onFieldsChange}
      >
        <Form.Item name="email" rules={formRules.email}>
          <Input prefix={<UserOutlined />} placeholder="Email" size="large" />
        </Form.Item>

        <Form.Item>
          <SubmitButton
            type="primary"
            htmlType="submit"
            block
            loading={loading}
            disabled={!isFormValid}
          >
            {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
          </SubmitButton>
        </Form.Item>
      </StyledForm>

      <FooterText>
        <Link to="/auth/login">Back to login</Link>
      </FooterText>
    </>
  )
}

export default ForgotPasswordView
